import { useEffect, useState } from 'react';
import { SpatialMap, WorldPosition } from './protocol';

export interface VenueAtlas extends SpatialMap {
  revision: string;
  minX: number; maxX: number; minZ: number; maxZ: number;
}
export const ATLAS_ROOT = `${process.env.PUBLIC_URL || ''}/maps/intersect-20260922`;

/** Explicitly version-matched: never put Intersect art over an unrelated/new layout. */
export function supportsIntersectAtlas(layoutUrl?: string): boolean {
  return /\/world_upc_intersect_hub_260922_00(?:[?#]|$)/i.test(layoutUrl || '');
}

export function useVenueAtlas(enabled: boolean) {
  const [atlas, setAtlas] = useState<VenueAtlas>();
  const [error, setError] = useState('');
  useEffect(() => {
    if (!enabled) { setAtlas(undefined); return; }
    const controller = new AbortController();
    setError('');
    fetch(`${ATLAS_ROOT}/layout.json`, { signal: controller.signal })
      .then(response => { if (!response.ok) throw new Error('Map download failed.'); return response.json(); })
      .then((data: VenueAtlas) => {
        if (!data.vertices?.length || !data.indices?.length || data.revision !== 'intersect-20260922') throw new Error('Map export is incomplete.');
        setAtlas(data);
      }).catch(reason => { if (!controller.signal.aborted) setError(String(reason.message)); });
    return () => controller.abort();
  }, [enabled]);
  return { atlas: enabled ? atlas : undefined, error };
}

/** Height on a walkable triangle; keeps preview movement on the exported floor/ramp. */
export function groundAt(map: SpatialMap, point: WorldPosition): WorldPosition | undefined {
  let best: WorldPosition | undefined;
  for (let i = 0; i < map.indices.length; i += 3) {
    const [a, b, c] = map.indices.slice(i, i + 3).map(index => map.vertices[index]);
    const denom = (b.z - c.z) * (a.x - c.x) + (c.x - b.x) * (a.z - c.z);
    if (Math.abs(denom) < 0.00001) continue;
    const u = ((b.z - c.z) * (point.x - c.x) + (c.x - b.x) * (point.z - c.z)) / denom;
    const v = ((c.z - a.z) * (point.x - c.x) + (a.x - c.x) * (point.z - c.z)) / denom;
    if (u < -0.0001 || v < -0.0001 || u + v > 1.0001) continue;
    const y = u * a.y + v * b.y + (1 - u - v) * c.y;
    if (Math.abs(y - point.y) > 0.65) continue;
    if (!best || Math.abs(y - point.y) < Math.abs(best.y - point.y)) best = { ...point, y };
  }
  return best;
}

/** Small steps prevent crossing a thin wall between two otherwise valid endpoints. */
export function moveOnMap(map: SpatialMap, from: WorldPosition, dx: number, dz: number): WorldPosition {
  if (!dx && !dz) return from;
  let position = from;
  const steps = Math.max(1, Math.ceil(Math.hypot(dx, dz) / 0.08));
  for (let i = 0; i < steps; i++) {
    const next = groundAt(map, { ...position, x: position.x + dx / steps, z: position.z + dz / steps });
    if (!next) break;
    position = next;
  }
  return position;
}
